/** Roles de usuario: normalización de nombres (sin acentos) y cálculo del rol principal y de los flags admin/superadmin. */
import type { LoggedUserInnerwork, Role } from "@/types";

export type RolNombre =
  | "superadmin"
  | "administrador"
  | "gerente"
  | "jefe taller"
  | "operario"
  | "responsable agenda"
  | "asesor tecnico";

const normalizar = (s?: string | null) =>
  (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // Quita conectores para poder comparar nombres de rol ("Jefe de Taller" -> "jefe taller").
    .replace(/\b(?:de|del)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const normalizarRol = (s?: string | null) => normalizar(s) as RolNombre;

/** True si alguno de los roles trae el flag `esSuperAdmin` del backend. */
export const esSuperAdmin = (user?: LoggedUserInnerwork | null): boolean =>
  !!user?.roles?.some((r) => r.esSuperAdmin === true);

export const tieneRol = (
  user: LoggedUserInnerwork | null | undefined,
  ...nombres: RolNombre[]
): boolean => {
  if (!user?.roles?.length) return false;
  const buscados = new Set(nombres.map((n) => normalizar(n)));
  return user.roles.some((r) => {
    if (r.esAdmin === true && buscados.has("administrador")) return true;
    if (r.esSuperAdmin === true && buscados.has("superadmin")) return true;
    return buscados.has(normalizar(r.nombre));
  });
};

export const distinguirRol = (roles: Role[] = []): RolNombre | undefined => {
  const nombres = new Set(roles.map((r) => normalizar(r.nombre)));
  const flagsSuper = roles.some((r) => r.esSuperAdmin === true);
  const flagsAdmin = roles.some((r) => r.esAdmin === true);

  const orden: RolNombre[] = [
    "superadmin",
    "gerente",
    "jefe taller",
    "responsable agenda",
    "asesor tecnico",
    "operario",
  ];

  for (const rol of orden) {
    if (
      rol === "superadmin" &&
      (flagsSuper || nombres.has("superadmin") || nombres.has("administrador"))
    ) {
      return "superadmin";
    }
    if (nombres.has(rol)) return rol;
  }
  if (flagsAdmin) return "gerente";

  return undefined;
};

/** Nombre original del rol principal (misma precedencia que `distinguirRol`), tal como viene del backend. */
export const nombreRolPrincipal = (roles: Role[] = []): string | undefined => {
  const principal = distinguirRol(roles);

  const coincide = (r: Role) =>
    principal === "superadmin"
      ? r.esSuperAdmin === true ||
        normalizar(r.nombre) === "superadmin" ||
        normalizar(r.nombre) === "administrador"
      : normalizar(r.nombre) === principal;

  const encontrado = principal ? roles.find(coincide) : undefined;
  if (encontrado?.nombre) return encontrado.nombre;
  // Sin match (rol derivado por flags o nombre desconocido): capitaliza la clave o usa el primer rol.
  if (principal) return principal.charAt(0).toUpperCase() + principal.slice(1);
  return roles[0]?.nombre;
};
